import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import { createIdBatcher } from './request-batch.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Kanban (R4): typovany klient nad `/v1/cases*` (generovany z
 * docs/api/openapi.json - viz @reos/api-client). `canUpdate`/`allowedTransitions`
 * chodi z API (policy engine / state-machine) - UI je jen cte, nikdy si je
 * nedopocitava (zadani: "nesuplikuj state machine v UI").
 */

export type KanbanCase = ApiPaths['/v1/cases']['get']['responses']['200']['content']['application/json']['items'][number];
export type CaseTimelineItem =
  ApiPaths['/v1/cases/{id}/timeline']['get']['responses']['200']['content']['application/json']['items'][number];
export type CaseTask = ApiPaths['/v1/cases/{id}/tasks']['get']['responses']['200']['content']['application/json']['items'][number];
export type CaseParticipant =
  ApiPaths['/v1/cases/{id}/participants']['get']['responses']['200']['content']['application/json'][number];
export type AttentionItem =
  ApiPaths['/v1/attention-items']['get']['responses']['200']['content']['application/json']['items'][number];
export type CaseSla = ApiPaths['/v1/cases/{id}/sla']['get']['responses']['200']['content']['application/json'];

export interface ListCasesParams {
  readonly type?: KanbanCase['type'] | undefined;
  readonly stage?: string | undefined;
  readonly propertyId?: string | undefined;
  /**
   * Krok APP2 bod 1: ZNAMA MNOZINA NEMOVITOSTI. Tabulky nakladu znaji
   * `propertyId` radku a chteji k nemu nazev zakazky — misto skenu cele
   * kancelare (`fetchCaseAddressIndex`) jde jeden dotaz na ta id.
   */
  readonly propertyIds?: readonly string[] | undefined;
  readonly participantPartyId?: string | undefined;
  /** "Moje zakazky" filtr (ADM1 bod 6 - K doplneni). */
  readonly ownerUserId?: string | undefined;
  /**
   * Krok QAFIX4b bod 3 (mezera POB1): filtr podle POBOCKY. Je to jen ZUZENI
   * nad dosahem, ktery uz urcil server (`brokerOfficeListScope`) — makler se
   * `broker_read_scope='office'` si cizi pobocku pres nej neotevre.
   */
  readonly officeId?: string | undefined;
  /**
   * Krok PROP3B (oprava e2e 1): hledani podle nazvu zakazky NA SERVERU
   * (nejmene 2 znaky). Pouziva ho `searchCasesByName`
   * (`lib/case-address-lookup.ts`) — jina obrazovka si vlastni hledani
   * psat nema.
   */
  readonly q?: string | undefined;
  /**
   * Krok NAB4 (oprava e2e 1): `updated_desc` = POSLEDNI ZMENENE zakazky.
   * Vychozi `created_asc` je strankovaci poradi, takze `limit` v nem urizne
   * nejnovejsi dění — s nim byl blok „zakázky v pohybu" v kancelari s tisici
   * zakazkami trvale prazdny. Razeni podle zmeny je JEDNA STRANKA
   * (`nextCursor` je vzdy `null`).
   */
  readonly sort?: 'created_asc' | 'updated_desc' | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

/**
 * Krok APP2 bod 1 — ZAKAZKY KLIENTU SE PTAJI DAVKOU, NE PO RADKU.
 *
 * Seznam `/klienti` se ptal `?participantPartyId=` na kazdeho klienta zvlast
 * (dvacet dotazu na stranku) a na roli pak jeste `:id/participants` (dalsich
 * dvacet). Davkova cesta (`GET /v1/cases/by-participants`) vrati zakazky vsech
 * stran JEDNIM dotazem a roli nese rovnou v `participantRole` — je to atribut
 * VAZBY klient–zakazka, takze druhy dotaz uz netreba.
 *
 * Nejvyssi `limitPerParty` serveru je 25; pres nej se dotaz posila primo.
 * Davkova cesta je JEDNA STRANKA (`nextCursor: null`) — kdo chce strankovat,
 * strankuje puvodnim dotazem (tentyz dusledek jako razeni `updated_desc`).
 */
const PARTICIPANT_BATCH_MAX_LIMIT = 25;

function batcherForLimit(limit: number): (partyId: string) => Promise<KanbanCase[]> {
  const ready = participantBatchers.get(limit);
  if (ready !== undefined) return ready;
  const batcher = createIdBatcher<KanbanCase[]>(async (partyIds) => {
    const result = await unwrap(
      apiClient.GET('/v1/cases/by-participants', {
        params: { query: { partyIds: partyIds.join(','), limitPerParty: limit } },
      }),
    );
    const byParty = new Map<string, KanbanCase[]>();
    for (const item of result.items) {
      const list = byParty.get(item.partyId) ?? [];
      list.push(item.case);
      byParty.set(item.partyId, list);
    }
    return byParty;
  }, () => []);
  participantBatchers.set(limit, batcher);
  return batcher;
}

/** Jeden davkovac na kazdy pouzity `limit` — mene polozek v davce by ochudilo volajiciho. */
const participantBatchers = new Map<number, (partyId: string) => Promise<KanbanCase[]>>();

/** Je to dotaz „zakazky teto strany" bez dalsich filtru? (Jen takovy jde davkovat.) */
function isParticipantOnlyQuery(params: ListCasesParams): params is ListCasesParams & {
  participantPartyId: string;
} {
  return (
    params.participantPartyId !== undefined &&
    (params.limit ?? 100) <= PARTICIPANT_BATCH_MAX_LIMIT &&
    params.type === undefined &&
    params.stage === undefined &&
    params.propertyId === undefined &&
    params.ownerUserId === undefined &&
    params.officeId === undefined &&
    params.q === undefined &&
    params.sort === undefined &&
    params.cursor === undefined
  );
}

export async function listCases(params: ListCasesParams = {}): Promise<{ items: KanbanCase[]; nextCursor: string | null }> {
  if (isParticipantOnlyQuery(params)) {
    const items = await batcherForLimit(params.limit ?? 1)(params.participantPartyId);
    return { items: [...items], nextCursor: null };
  }
  return unwrap(
    apiClient.GET('/v1/cases', {
      params: {
        query: {
          limit: params.limit ?? 100,
          ...(params.type !== undefined ? { type: params.type } : {}),
          ...(params.stage !== undefined ? { stage: params.stage } : {}),
          ...(params.propertyId !== undefined ? { propertyId: params.propertyId } : {}),
          ...(params.propertyIds !== undefined ? { propertyIds: params.propertyIds.join(',') } : {}),
          ...(params.participantPartyId !== undefined ? { participantPartyId: params.participantPartyId } : {}),
          ...(params.ownerUserId !== undefined ? { ownerUserId: params.ownerUserId } : {}),
          ...(params.officeId !== undefined ? { officeId: params.officeId } : {}),
          ...(params.q !== undefined ? { q: params.q } : {}),
          ...(params.sort !== undefined ? { sort: params.sort } : {}),
          ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
        },
      },
    }),
  );
}

/**
 * Strop POCTU POZADAVKU pri prochazeni celeho seznamu zakazek — JEDEN pro
 * vsechny takove prehledy (vyber zakazky u faktury a smlouvy, tabulky
 * nakladu, „Ekonomika kanceláře", vychozi filtr „moje zakázky").
 *
 * Proc tak vysoko: `GET /v1/cases` strankuje VZESTUPNE (`order by created_at
 * asc`, viz `packages/case/src/internal/service.ts`), takze nizky strop
 * neuriznul zakazky „nekde vzadu", ale prave ty NEJNOVEJSI — se kterymi
 * makler pracuje. Demo kancelar mela 11. 10. 2026 2095 zakazek a strop
 * 20 stran (= 2000) zahodil posledni stranu: zakazka zalozena o minutu driv
 * se v nabidce „Ze zakázky" uz nenasla (e2e S18c, krok UXP6).
 * Dokud server nenabizi hledani podle nazvu, je strop jen pojistka proti
 * nekonecnemu cyklu, ne limit dosahu.
 */
export const CASE_SCAN_MAX_PAGES = 100;

/** Id vsech zakazek prihlaseneho uzivatele (ADM1 bod 6 - vychozi filtr "moje zakazky"). */
export async function fetchOwnCaseIds(ownerUserId: string): Promise<readonly string[]> {
  const ids: string[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < CASE_SCAN_MAX_PAGES; page += 1) {
    const result = await listCases({ ownerUserId, limit: 100, ...(cursor !== null ? { cursor } : {}) });
    ids.push(...result.items.map((c) => c.id));
    if (result.nextCursor === null) break;
    cursor = result.nextCursor;
  }
  return ids;
}

export async function getCase(id: string): Promise<KanbanCase> {
  return unwrap(apiClient.GET('/v1/cases/{id}', { params: { path: { id } } }));
}

/**
 * Krok UXP1 (nalez P1-8) — „Upravit" u zakazky. Meni se jen nazev a zadani
 * makléře; stage jde pres `transitionCase`, strany pres participants.
 * Vynechane pole = nemenit (server bere `undefined` jako „nech jak je").
 */
export async function updateCase(
  id: string,
  patch: {
    readonly title?: string | undefined;
    readonly description?: string | null | undefined;
    /** Krok QAFIX4b bod 3: prerazeni zakazky na jinou pobocku (`null` = zadna). */
    readonly officeId?: string | null | undefined;
  },
): Promise<KanbanCase> {
  return unwrap(
    apiClient.PATCH('/v1/cases/{id}', {
      params: { path: { id } },
      body: {
        ...(patch.title !== undefined ? { title: patch.title } : {}),
        ...(patch.description !== undefined ? { description: patch.description } : {}),
        ...(patch.officeId !== undefined ? { officeId: patch.officeId } : {}),
      },
    }),
  );
}

export async function transitionCase(id: string, toStage: string, reason?: string): Promise<KanbanCase> {
  return unwrap(
    apiClient.POST('/v1/cases/{id}/transition', {
      params: { path: { id } },
      body: { toStage, ...(reason !== undefined ? { reason } : {}) },
    }),
  );
}

/**
 * Krok SEZ1 bod 2: zapis do historie zakazky (`case_events` kind `note`).
 * Vetu z nej uz umi `caseEventSentence`, takze je videt v „Poslední dění"
 * i na zalozce Aktivity — zadna nova tabulka (viz ROZHODNUTI SEZ1-ZAPIS).
 */
export async function addCaseNote(id: string, text: string): Promise<void> {
  await unwrap(apiClient.POST('/v1/cases/{id}/notes', { params: { path: { id } }, body: { text } }));
}

export async function listCaseTimeline(id: string): Promise<CaseTimelineItem[]> {
  const result = await unwrap(apiClient.GET('/v1/cases/{id}/timeline', { params: { path: { id } } }));
  return result.items;
}

export async function listCaseTasks(id: string): Promise<CaseTask[]> {
  const result = await unwrap(apiClient.GET('/v1/cases/{id}/tasks', { params: { path: { id } } }));
  return result.items;
}

export async function listCaseParticipants(id: string): Promise<CaseParticipant[]> {
  return unwrap(apiClient.GET('/v1/cases/{id}/participants', { params: { path: { id } } }));
}

export async function listAttentionItems(caseId?: string): Promise<AttentionItem[]> {
  const result = await unwrap(
    apiClient.GET('/v1/attention-items', { params: { query: caseId !== undefined ? { caseId } : {} } }),
  );
  return result.items;
}

export async function getCaseSla(id: string): Promise<CaseSla> {
  return unwrap(apiClient.GET('/v1/cases/{id}/sla', { params: { path: { id } } }));
}

/**
 * Krok ZAK1 bod 2 — „Načíst znovu" udaje o nemovitosti z otevrenych dat.
 *
 * Odpoved je 202: prace visi na udalosti a bezi ve workeru, takze tady nejde
 * o hodnoty, ale o zarazeni. Stav si obrazovka cte z casove osy zakazky
 * (`prefillStatus` v `case-last-event.ts`) — jediny zdroj pravdy o tom, kdy
 * se naposledy nacitalo.
 */
export async function requestPropertyPrefill(id: string): Promise<'queued' | 'no_property'> {
  const result = await unwrap(
    apiClient.POST('/v1/cases/{id}/property-prefill', { params: { path: { id } } }),
  );
  return result.state;
}
